import { useQueryClient } from "@tanstack/react-query";
import { DocumentKeys } from "web_myplann/Api/Document/DocumentKeys";
import { useDocumentUploadMutation } from "web_myplann/Api/Document/useDocumentUploadMutation";
import { base64ToFile } from "web_myplann/Infrastructure/Utils/FileUtils";
import { device } from "web_myplann/Utils/device";

type Options = {
	onUploaded: () => void;
};

type PickAndUploadDocument = {
	pickPhoto: () => Promise<void>;
	pickGallery: () => Promise<void>;
	pickFile: () => Promise<void>;
	isPending: boolean;
};

export const usePickAndUploadDocument = ({
	onUploaded,
}: Options): PickAndUploadDocument => {
	const { mutate: uploadDocument, isPending } = useDocumentUploadMutation();
	const queryClient = useQueryClient();

	const uploadPicked = (base64?: string, filename?: string) => {
		if (!base64 || !filename) {
			return;
		}

		uploadDocument(
			{
				data: {
					title: filename,
					file: base64ToFile(base64, filename),
				},
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries({ queryKey: DocumentKeys.allLists });
					onUploaded();
				},
			},
		);
	};

	const pickPhoto = async () => {
		const response = await device.pickAttachmentPhoto();
		uploadPicked(response.payload.file.base64, response.payload.file.filename);
	};

	const pickGallery = async () => {
		const response = await device.pickAttachmentGallery();
		uploadPicked(response.payload.file.base64, response.payload.file.filename);
	};

	const pickFile = async () => {
		const response = await device.pickAttachmentFile();
		uploadPicked(response.payload.file.base64, response.payload.file.filename);
	};

	return { pickPhoto, pickGallery, pickFile, isPending };
};
